import { FermentedVegetableStart, PicklesPhControl } from "../service";

export enum FoodManagementControls {
  freezer = "freezer",
  cooler = "cooler",
  fishCooler = "fishCooler",
  coldRoom = "coldRoom",
  heatingTreatment = "heatingTreatment",
  heatRetetion = "heatRetention",
  coolingFood = "coolingFood",
  reheatingFood = "reheatingFood",
  cleaningFloor = "cleaningFloor",
  cleaningHood = "cleaningHood",
  cleaningDishwasher = "cleaningDishwasher",
  revisionOfControls = "revisionOfControls",
  pestsRevision = "pestsRevision",
  pestsRouding = "pestsRouding",
  maintenanceExamination = "maintenanceExamination",
  thermometerVerification = "thermometerVerification",
  goodsReceipt = "goodsReceipt",
  vegetablespHbeginning = "vegetablespHbeginning",
  vegetablespH48Hours = "vegetablespH48Hours",
  vegetablespHfinal = "vegetablespHfinal",
  picklespH = "picklespH",
  sushiricepH = "sushiricepH",
  openbuffet = "openbuffet",
  freezingfish = "freezingfish",
}

export enum ControlPeriodType {
  weekly = "1 Week",
  twoWeekly = "2 Weeks",
  monthly = "1 Month",
  yearly = "1 Year",
}

export interface ControlPeriod {
  id?: string;
  description: ControlPeriodType;
  durationInDays: number;
}

export enum InstanceStatus {
  waiting = "waiting",
  completed = "completed",
  expired = "expired",
  completedWithErrors = "completedWithErrors",
  overdueSubmitted = "overdueSubmitted",
  empty = "empty", // used for deciding if the instance is newly created or not
}

export enum StatusBgColors {
  completed = "bg-success-light",
  expired = "bg-danger-light",
  completedWithErrors = "bg-warning-light",
}

type ButtonColor = "success" | "danger" | "warning" | "info" | undefined;

export const buttonColorMap: Partial<Record<InstanceStatus, ButtonColor>> = {
  completed: "success",
  expired: "danger",
  completedWithErrors: "warning",
};

export interface FoodControlPointInstance {
  id?: string;
  companyId: string;
  companyLocationId: string;
  foodControlPointId: string;
  updatedDate?: string;
  status: InstanceStatus;
  startDate: string;
  endDate: string;
  isExpired?: boolean;
  validationData?: string;
  isExplanationFilled?: boolean | null;
  reason?: string;
  remark?: string;
  data?: string;
  isDeleted?: boolean;
}

export interface FoodControlPoint {
  id: string;
  companyId: string;
  companyLocationId: string;
  controlPeriodId: string;
  status: string;
  name: string;
  type: FoodManagementControls;
  validationRules: string;
  isDeleted?: boolean;
}

export interface FoodControlPointInstanceDto extends FoodControlPointInstance {
  controlPoint?: FoodControlPoint;
}

export interface FoodReport {
  id: string;
  name: string;
  type: FoodManagementControls;
  controlPeriod?: ControlPeriod;
  foodControlPointInstances: FoodControlPointInstance[];
}

export type FoodControlBaseData = {
  id?: string;
  companyId: string;
  companyLocationId: string;
  controlPointInstanceId?: string;
  status: InstanceStatus;
  note?: string;
  creationDate?: string;
  registeredBy?: string;
};

export type FoodProductionControl = FoodControlBaseData & {
  startDateTime?: string;
  endDateTime?: string;
  dishName: string;
  startTemperature: number;
  finalTemperature: number;
  validation?: boolean;
};

export type FoodTemperatureControl = FoodControlBaseData & {
  temperature: number;
  validation?: boolean;
};

export type FoodCleaning = FoodControlBaseData & {
  temperature: number;
};

export type PestsRounding = FoodControlBaseData & {
  value: string;
};

export type PestsRevision = FoodControlBaseData & {
  validateNet: number;
  validateDoorAndWindow: number;
  validateSewers: number;
  validateWallStorage: number;
  validatePestEntrange: number;
  fixedAgreement: string;
  contact: string;
};

export type MaintenanceExam = FoodControlBaseData & {
  validateThermometer: number;
  validateDoorAndWindow: number;
  validateSewers: number;
  validateCleaning: number;
  validatePests: number;
  validateWallAndFloor: number;
};

export type Item = FoodControlBaseData & {
  acceptanceDate?: string;
  validateMarking: number;
  validateExpirety: number;
  validatePackaging: number;
  validateQuality: number;
  validateTemperature: number;
  supplierId: string;
  file?: string;
  goodsName: string;
  temperature: number;
  goodType: string;
};

export type ProgramRevision = FoodControlBaseData & {
  validateActivities: number;
  validateMistakes: number;
  validateEmployees: number;
  validateEmployees2: number;
  validateThermometer: number;
  validateProduction: number;
  validateCleaning: number;
  validateCleaning2: number;
  validatePests: number;
  validateMaintenance: number;
};

export type ThermometerVerification = FoodControlBaseData & {
  value: string;
};

export type FreezingFish = FoodControlBaseData & {
  selectedTextId: number;
};

export type OpenBuffet = FoodControlBaseData & {
  selectedTextId: number;
  startTime: string;
  endTime: string;
  nameOfProducts: string;
};

export type SushiRice = FoodControlBaseData & {
  selectedPhId: string;
};

export type FoodControl =
  | Item
  | FoodTemperatureControl
  | FoodProductionControl
  | FoodCleaning
  | PestsRevision
  | PestsRounding
  | ProgramRevision
  | MaintenanceExam
  | ThermometerVerification
  | FermentedVegetableStart
  | PicklesPhControl
  | undefined;

export type ControlStatus = {
  status: string;
  type: "info" | "warning" | "success" | "danger";
};

export type ControlPointCategory =
  | "Inventory Controls"
  | "Food Acceptance Controls"
  | "Food Processing Controls"
  | "Dishwasher Controls"
  | "Yearly or Other Controls"
  | "Unknown Category";

export type ControlPointReportCategory =
  | "Inventory Controls"
  | "Food Acceptance Controls"
  | "Food Processing Controls"
  | "Cleaning Controls"
  | "Dishwasher Controls"
  | "Yearly or Other Controls"
  | "Unknown Category";
